import { Box, Typography } from '@mui/material';
import { useEffect, useMemo } from 'react';
import { useTranslate } from 'react-admin';
import { useFormContext, useWatch } from 'react-hook-form';

import { B2bCompanyPaymentMethod, PaymentMethodIntent } from '@Common/types/bo/b2b/companies';

import { ShowSection } from '@sh4res/bo-common/components';
import {
  isExternalBankDisabled,
  isSepaDirectDebitDisabled,
} from '@sh4res/bo-common/components/B2bCompanyPaymentMethods/company_payment_methods/payment_method_availability';
import {
  paymentMethodIntentChoices,
  paymentMethodTypeChoices,
} from '@sh4res/bo-common/components/B2bCompanyPaymentMethods/company_payment_methods/type';
import { RadioButtonGroupInput } from '@sh4res/bo-common/components/RadioButtonGroupInput';

// 'invoicing' is not creatable from the BO
const creatableIntents: PaymentMethodIntent[] = ['principal', 'ccb_withdrawal'];

const intentChoices = paymentMethodIntentChoices.filter((choice) =>
  creatableIntents.includes(choice.id),
);

export const PaymentMethodStep = ({
  paymentMethods,
  hasCcb,
}: {
  paymentMethods: B2bCompanyPaymentMethod[] | undefined;
  hasCcb: boolean;
}) => {
  const translate = useTranslate();
  const { getValues, setValue } = useFormContext();
  const watchedIntent: PaymentMethodIntent = useWatch<{
    payment_method_intent: PaymentMethodIntent;
  }>({
    name: 'payment_method_intent',
  });
  // without a CCB only the principal intent is available, whatever was stored in the form
  const intent: PaymentMethodIntent = hasCcb ? watchedIntent : 'principal';

  const isSepaDisabled = useMemo(
    () => isSepaDirectDebitDisabled(paymentMethods, intent),
    [paymentMethods, intent],
  );
  const isExternalBankTypeDisabled = useMemo(
    () => isExternalBankDisabled(paymentMethods, intent),
    [paymentMethods, intent],
  );

  const typeChoices = useMemo(
    () =>
      paymentMethodTypeChoices.map((choice) => ({
        ...choice,
        disabled:
          (choice.id === 'sepa_direct_debit' && isSepaDisabled) ||
          (choice.id === 'external_bank' && isExternalBankTypeDisabled),
      })),
    [isSepaDisabled, isExternalBankTypeDisabled],
  );

  // Switching the intent can leave a type selected that is already taken for the new intent
  useEffect(() => {
    const selectedType = getValues('payment_method_type');
    if (
      (selectedType === 'sepa_direct_debit' && isSepaDisabled) ||
      (selectedType === 'external_bank' && isExternalBankTypeDisabled)
    ) {
      setValue('payment_method_type', undefined);
    }
  }, [getValues, setValue, isSepaDisabled, isExternalBankTypeDisabled]);

  return (
    <Box>
      {hasCcb && (
        <>
          <Typography>
            {translate(
              'resources.bo_common.b2b.companies.payment_methods.sections.payment_method.intent',
            )}
          </Typography>
          <ShowSection
            leftAligned
            label="resources.bo_common.b2b.companies.payment_methods.sections.payment_method.payment_method_intent"
            noCard={true}>
            <RadioButtonGroupInput
              source="payment_method_intent"
              choices={intentChoices}
              required
              data-testid="BoExternalB2bCompanyPaymentMethodStep_PaymentMethodIntentInput"
            />
          </ShowSection>
        </>
      )}
      <ShowSection
        leftAligned
        label="resources.bo_common.b2b.companies.payment_methods.sections.payment_method.payment_method_type"
        noCard={true}>
        <RadioButtonGroupInput
          source="payment_method_type"
          choices={typeChoices}
          required
          data-testid="BoExternalB2bCompanyPaymentMethodStep_PaymentMethodTypeInput"
        />
      </ShowSection>
    </Box>
  );
};
